import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Modal, Form, Input, Checkbox, Space, Select, message } from "antd";
import { Link, useSearchParams } from "react-router-dom";
import styled, { css, createGlobalStyle } from "styled-components";
import Button from "./Button";
import { APP_NAME } from "../helpers/constants";
import { Spacer } from "../styles/CommonElements";
import { trackEvent } from "../helpers/mixpanel";
import { discordTrack } from "../lib/analytics/discordTrack";
import { APP_TERMS_ROUTE } from "../helpers/routeNames";
import {
  getPricingSchedule,
  getPricingScheduleWithFree,
  formatMonthlyUsd,
  getAnnualDisplayUsd,
  formatAnnualDisplayUsd,
} from "../lib/pricing";
import { submitFreeGatewaySignup } from "../lib/api/freeGatewaySignup";

const { TextArea } = Input;
const { Option } = Select;

// Legacy alias: older bundles or half-merged files may still reference `<PhoneInput />` after
// the phone field was removed; a plain `Input` avoids ReferenceError. Safe to delete once
// no usage remains anywhere in the tree.
// eslint-disable-next-line no-unused-vars -- may be used only in some build/HMR states
const PhoneInput = Input;

/** Checkout billing cycle is chosen in-modal (or seeded from the pricing page). */

function buildIntakePlanOptions(schedule) {
  const keys = Object.keys(schedule).filter(
    (k) => k !== "enterprise" && k !== "business"
  );
  const order = [
    "free",
    "starter",
    "pro",
    "growth",
    "scale",
  ];
  const sortedKeys = [
    ...order.filter((k) => keys.includes(k)),
    ...keys.filter((k) => !order.includes(k)),
  ];
  return sortedKeys.map((key) => {
    const tier = schedule[key];
    if (!tier) return null;
    return {
      label: tier.name || key,
      value: key,
      price: tier.monthlyUSD,
      units: tier.unitCap,
    };
  }).filter(Boolean);
}

/**
 * Normalize a plan key for UI display (removes chat_ prefix so the suite plan
 * options in the dropdown match). Does NOT change what's sent to the backend.
 */
function normalizePlanForUnifiedCheckout(plan) {
  const raw = String(plan || "").trim().toLowerCase();
  if (raw === "trial") return "trial";
  if (raw === "free") return "free";
  if (raw.startsWith("chat_")) {
    const map = {
      chat_starter: "starter",
      chat_growth: "growth",
      chat_scale: "scale",
      chat_pro: "growth",
      chat_business: "growth",
    };
    return map[raw] || "starter";
  }
  if (raw === "business") return "scale";
  if (
    raw === "starter" ||
    raw === "growth" ||
    raw === "scale" ||
    raw === "pro"
  ) {
    return raw;
  }
  /** Enterprise is quote-based only; closest self-serve tier for intake preselect. */
  if (raw === "enterprise") return "scale";
  return "starter";
}

/**
 * Return the plan key to send to the backend / Stripe.
 *
 * Checkout writes suite keys (starter / growth / scale). Every band provisions
 * the full LandlordOps OS; Stripe price env vars still fall back to the
 * historical LEO_CHAT_* IDs when suite-specific prices are unset.
 */
function deriveCheckoutPlanKey(displayPlan) {
  const p = String(displayPlan || "").trim().toLowerCase();
  if (p === "chat_starter" || p === "starter") return "starter";
  if (
    p === "chat_growth" ||
    p === "chat_business" ||
    p === "chat_pro" ||
    p === "growth" ||
    p === "pro"
  ) {
    return "growth";
  }
  if (p === "chat_scale" || p === "business" || p === "scale") return "scale";
  return "starter";
}

/**
 * Product line shown before the tier name in the plan dropdown + modal header.
 * Every self-serve tier is LandlordOps (full OS); door caps differ by plan.
 */
function getOpsChatBrand(_suiteKey) {
  return "LandlordOps";
}

/** Inset from viewport edges (safe areas + fixed margin) so the intake never hugs top/bottom. */
const IntakeModalWrapGlobal = createGlobalStyle`
  .intake-modal-wrap {
    padding: max(1.25rem, env(safe-area-inset-top, 0px))
      max(1rem, env(safe-area-inset-right, 0px))
      max(1.25rem, env(safe-area-inset-bottom, 0px))
      max(1rem, env(safe-area-inset-left, 0px)) !important;
    box-sizing: border-box;
  }

  @media (min-width: 769px) {
    .intake-modal-wrap {
      padding: max(1.5rem, env(safe-area-inset-top, 0px))
        max(1.5rem, env(safe-area-inset-right, 0px))
        max(1.5rem, env(safe-area-inset-bottom, 0px))
        max(1.5rem, env(safe-area-inset-left, 0px)) !important;
    }
  }

  /* Desktop intake width; always capped to viewport */
  .intake-modal-wrap .ant-modal {
    top: 0 !important;
    margin: 0 auto;
    padding-bottom: 0;
    width: 100% !important;
    max-width: min(778px, calc(100vw - 2rem)) !important;
    /* Wrap padding (top+bottom) + safe areas; 3.5rem ≈ mobile 2×1.25 + buffer */
    max-height: calc(
      100dvh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px) - 3.5rem
    );
  }

  @media (max-width: 400px) {
    .intake-modal-wrap .ant-modal {
      max-width: calc(100vw - 1.5rem) !important;
    }
  }

  .intake-modal-wrap .ant-modal-content {
    max-height: calc(
      100dvh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px) - 3.5rem
    ) !important;
    display: flex !important;
    flex-direction: column !important;
    overflow: hidden !important;
  }

  .intake-modal-wrap .ant-modal-header {
    flex-shrink: 0 !important;
  }

  .intake-modal-wrap .ant-modal-body {
    flex: 1 1 auto !important;
    min-height: 0 !important;
    overflow-y: auto !important;
    -webkit-overflow-scrolling: touch;
  }
`;

const IntakeModal = ({
  isOpen,
  onClose,
  selectedPlan: initialSelectedPlan,
  onSubmit,
  /** `monthly` | `annual` (must match the pricing page toggle). */
  checkoutBillingCycle = "monthly",
  /** Merged into Stripe checkout request metadata (e.g. pricingTierGroup, source). */
  extraCheckoutMetadata = {},
  /** @deprecated Ignored; unified pricing ladder only. Kept for backward compatibility with older callers. */
  intakeMode: _deprecatedIntakeMode = "pm",
}) => {
  const isTrialIntake =
    typeof initialSelectedPlan === "string" &&
    initialSelectedPlan.trim().toLowerCase() === "trial";

  /** True when this intake was opened from an OpsChat context (chat_* plan key). */
  const isOpsChatCheckout =
    typeof initialSelectedPlan === "string" &&
    initialSelectedPlan.trim().toLowerCase().startsWith("chat_");

  const pricingScheduleGroup =
    extraCheckoutMetadata?.pricingTierGroup === "legacy_2025"
      ? "legacy_2025"
      : "market_2026";
  const intakePricingSchedule = useMemo(() => {
    if (pricingScheduleGroup === "legacy_2025") {
      return getPricingSchedule("legacy_2025");
    }
    return getPricingScheduleWithFree(pricingScheduleGroup);
  }, [pricingScheduleGroup]);

  const [searchParams] = useSearchParams();
  const inviteCode = searchParams.get("invite");

  const planOptions = useMemo(
    () => {
      const base = buildIntakePlanOptions(intakePricingSchedule);
      // Prefix tier with product line (OpsChat vs LandlordOps).
      return base.map((opt) => ({
        ...opt,
        label: `${getOpsChatBrand(opt.value)} ${opt.label}`,
      }));
    },
    [intakePricingSchedule]
  );

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(() =>
    initialSelectedPlan
      ? normalizePlanForUnifiedCheckout(initialSelectedPlan)
      : "starter"
  );
  const [formValid, setFormValid] = useState(false);
  const [billingInterval, setBillingInterval] = useState(() =>
    checkoutBillingCycle === "annual" ? "annual" : "monthly"
  );

  const hasPaidPlanOptions = useMemo(
    () => planOptions.some((o) => o.price > 0),
    [planOptions]
  );

  const formatPlanOptionPrice = useCallback(
    (opt) => {
      if (opt.price === 0) {
        return (
          <>
            FREE • Up to {opt.units} doors
          </>
        );
      }
      if (billingInterval === "annual") {
        const annualUsd = getAnnualDisplayUsd(opt.value, pricingScheduleGroup);
        if (annualUsd != null) {
          return (
            <>
              ${formatAnnualDisplayUsd(annualUsd)}/year • 2 months free • Up to{" "}
              {opt.units} doors
            </>
          );
        }
      }
      return (
        <>
          ${formatMonthlyUsd(opt.price)}/month • Up to {opt.units} doors
        </>
      );
    },
    [billingInterval, pricingScheduleGroup]
  );

  const selectedTier = useMemo(() => {
    const tier = intakePricingSchedule[selectedPlan];
    if (tier) return tier;
    const fallbackKey = planOptions[0]?.value;
    return fallbackKey ? intakePricingSchedule[fallbackKey] : null;
  }, [intakePricingSchedule, selectedPlan, planOptions]);

  const planIncludesLines = useMemo(() => {
    const raw = selectedTier?.features;
    if (Array.isArray(raw) && raw.length > 0) return raw;
    const tag = selectedTier?.tagline;
    return tag ? [tag] : [];
  }, [selectedTier]);

  const planIncludesTitleName =
    selectedTier?.name ||
    planOptions.find((p) => p.value === selectedPlan)?.label ||
    selectedPlan;

  const planIncludesDoorLine = useMemo(() => {
    const first = planIncludesLines[0];
    if (!first || typeof first !== "string") return null;
    const t = first.trim().toLowerCase();
    return t.startsWith("up to ") && t.includes("door") ? first.trim() : null;
  }, [planIncludesLines]);

  const planIncludesFeatureLines = useMemo(() => {
    if (planIncludesDoorLine) return planIncludesLines.slice(1);
    return planIncludesLines;
  }, [planIncludesLines, planIncludesDoorLine]);

  useEffect(() => {
    if (isOpen) {
      form.resetFields();
      setSelectedPlan(
        initialSelectedPlan
          ? normalizePlanForUnifiedCheckout(initialSelectedPlan)
          : "starter"
      );
      setFormValid(false);
      setBillingInterval(
        checkoutBillingCycle === "annual" ? "annual" : "monthly"
      );
      // Ensure hidden fields still have expected shape
      form.setFieldsValue({ serviceRequirements: "", chatbotQuestions: "" });
      trackEvent("Intake Modal Viewed", {
        plan: initialSelectedPlan,
        modal_type: "unified_intake_form",
      });
    }
  }, [isOpen, form, initialSelectedPlan, checkoutBillingCycle]);

  // Check form validation on field changes
  const checkFormValidity = () => {
    const values = form.getFieldsValue();
    setFormValid(
      Boolean(
        values.name && values.email && values.agreeToTerms
      )
    );
  };

  const normalizeWebsiteUrl = (value) => {
    if (!value) return value;
    // Remove any existing protocol
    return value.replace(/^https?:\/\//, "");
  };

  const handleSubmit = async (values) => {
    if (isTrialIntake) {
      setLoading(true);
      try {
        if (typeof onSubmit === "function") await onSubmit(values);
      } catch (err) {
        console.error("[IntakeModal] Trial submit error:", err);
      } finally {
        setLoading(false);
      }
      return;
    }

    if (selectedPlan === "free") {
      setLoading(true);
      try {
        const parts = String(values.name || "")
          .trim()
          .split(/\s+/);
        await submitFreeGatewaySignup({
          email: values.email,
          firstName: parts[0] || "",
          lastName: parts.length > 1 ? parts.slice(1).join(" ") : "",
          phone: "",
          portfolioSize: "",
          propertyType: "other",
          experience: "0-1",
          pricingTierGroup: pricingScheduleGroup,
          inviteCode: inviteCode || undefined,
          agreeToTerms: values.agreeToTerms === true,
        });
        trackEvent("Intake Form Submitted", {
          plan: "free",
          has_custom_questions: false,
          has_service_requirements: false,
        });
        message.success(
          "Account created. Check your email for your temporary password, then sign in."
        );
        if (typeof onClose === "function") onClose();
      } catch (err) {
        console.error("[IntakeModal] Free intake error:", err);
        message.error(
          err?.message ||
            "Could not create your account. Try again or use a different email."
        );
      } finally {
        setLoading(false);
      }
      return;
    }

    // Validation for plan and billing cycle
    const validPlans = planOptions.map((p) => p.value);
    if (!validPlans.includes(selectedPlan)) {
      alert("Please select a valid plan.");
      setLoading(false);
      return;
    }

    const resolvedBillingCycle =
      billingInterval === "annual" ? "annual" : "monthly";

    trackEvent("Intake Form Submitted", {
      plan: selectedPlan,
      has_custom_questions: false,
      has_service_requirements: false,
      billing_cycle: resolvedBillingCycle,
    });

    setLoading(true);
    try {
      const propertyTypes = [];

      // Custom intake questions disabled for now
      const chatbotQuestions = [];

      // All plans (starter, growth, pro) are paid plans that go to Stripe checkout
      // No trial signup flow - all plans require payment

      // Compute top-level fields required by backend schema
      const derivedClientName =
        (values.clientName || values.fullName || values.name || "")
          .toString()
          .trim() || (values.email ? values.email.split("@")[0] : "");
      // Always resolves to suite SKU keys: starter / growth / scale.
      const checkoutPlanKey = deriveCheckoutPlanKey(selectedPlan);
      const normalizedSelectedPlan = checkoutPlanKey.toLowerCase();

      // For paid plans, create the request body and process Stripe checkout
      const requestBody = {
        email: values.email,
        clientName: derivedClientName,
        phone: "",
        portfolioSize: "",
        propertyTypes,
        billingCycle: resolvedBillingCycle,
        selectedPlan: normalizedSelectedPlan,
        priceId: getPriceId(normalizedSelectedPlan),
        metadata: {
          clientKey: values.clientKey || "",
          portfolioSize: "",
          propertyTypes,
          chatbotQuestions,
          billingCycle: resolvedBillingCycle,
        },
      };

      // Call the create-checkout-session endpoint (authenticated)
      const { firebaseAuth } = await import("../helpers/authentication");
      const currentUser = firebaseAuth?.currentUser;
      const idToken = currentUser ? await currentUser.getIdToken() : null;

      // Ensure landlordId is attached to metadata
      requestBody.metadata = {
        ...requestBody.metadata,
        landlordId: currentUser?.uid || requestBody.metadata?.landlordId || "",
        source: "intake_modal",
        referralCode: "",
        ...extraCheckoutMetadata,
      };

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/stripe/create-checkout-session`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
          },
          body: JSON.stringify(requestBody),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error("API Error Response:", errorData);
        throw new Error(
          errorData.error ||
            `Failed to create checkout session (HTTP ${response.status})`
        );
      }

      const { url } = await response.json();
      console.log("=== Submission Successful ===");
      console.log("Redirecting to:", url);

      trackEvent("Checkout Started", {
        plan: selectedPlan,
        has_custom_questions: false,
        has_service_requirements: false,
        billing_cycle: resolvedBillingCycle,
      });
      discordTrack("checkout_started", {
        plan: selectedPlan,
        billing_cycle: resolvedBillingCycle,
      });

      window.location.href = url;
    } catch (error) {
      console.error("=== Submission Error ===");
      console.error("Error details:", error);

      trackEvent("Intake Form Submission Error", {
        plan: selectedPlan,
        error_message: error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  // Map plan to backend-accepted price keys (no extra envs, matches API template)
  // Note: Backend handles actual Stripe price ID lookup via environment variables
  const getPriceId = (plan) => {
    const p = (plan || "").toLowerCase();
    const PRICE_BY_PLAN = {
      starter: "price_basic",
      growth: "price_growth",
      scale: "price_scale",
      pro: "price_pro",
      business: "price_business",
      chat_starter: "price_chat_starter",
      chat_growth: "price_chat_growth",
      chat_scale: "price_chat_scale",
      chat_pro: "price_chat_growth",
      chat_business: "price_chat_growth",
    };
    return PRICE_BY_PLAN[p] || "price_basic";
  };

  const handleModalClose = () => {
    trackEvent("Intake Modal Closed", {
      plan: selectedPlan,
      modal_type: "unified_intake_form",
      method: "cancel_button",
    });
    onClose();
  };

  const headerPlanLabel =
    planOptions.find((p) => p.value === selectedPlan)?.label || selectedPlan;

  // Brand always reflects which product the selected tier belongs to
  const headerBrand = getOpsChatBrand(selectedPlan);
  const isFreePlan = selectedPlan === "free";

  return (
    <>
      <IntakeModalWrapGlobal />
      <StyledModal
        wrapClassName="intake-modal-wrap"
        title={
          <ModalHeaderInner>
            <ModalEyebrow>Get started with</ModalEyebrow>
            <ModalHeadline>
              <ModalHeadlineBrand>{headerBrand}</ModalHeadlineBrand>
              <ModalHeadlineSep aria-hidden> - </ModalHeadlineSep>
              <ModalHeadlineTier>
                {/* Strip the brand prefix so header reads e.g. "OpsChat - Growth" not "OpsChat - OpsChat Growth" */}
                {headerPlanLabel.replace(/^(OpsChat|LandlordOps)\s+/i, "")}
              </ModalHeadlineTier>
            </ModalHeadline>
          </ModalHeaderInner>
        }
        open={isOpen}
        onCancel={handleModalClose}
        footer={null}
        width={778}
        style={{ maxWidth: 778 }}
        centered
        closable={true}
      >
      <StyledForm
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        onValuesChange={checkFormValidity}
        onFinishFailed={(errorInfo) => {
          console.error("Form submission failed:", errorInfo);
        }}
        requiredMark={true}
      >
        <FormSection>
          {/* Plan selection UI */}
          <Form.Item
            label="Select Plan"
            className={
              hasPaidPlanOptions ? "intake-select-plan-with-toggle" : undefined
            }
          >
            <PlanBillingRow>
              <Select
                className="intake-plan-select"
                popupClassName="intake-plan-select-dropdown"
                value={selectedPlan}
                onChange={(val) => {
                  setSelectedPlan(val);
                  setTimeout(() => checkFormValidity(), 0);
                }}
                size="large"
                style={{ width: "100%" }}
              >
                {planOptions.map((opt) => (
                  <Option key={opt.value} value={opt.value}>
                    <PlanOptionContent>
                      <PlanOptionName>{opt.label}</PlanOptionName>
                      <PlanOptionDetails>
                        {formatPlanOptionPrice(opt)}
                      </PlanOptionDetails>
                    </PlanOptionContent>
                  </Option>
                ))}
              </Select>
            </PlanBillingRow>
          </Form.Item>
          {hasPaidPlanOptions && (
            <IntakeBillingIntervalRow>
              <IntakeIntervalToggle
                role="group"
                aria-label="Choose monthly or annual billing"
              >
                <IntakeIntervalButton
                  type="button"
                  $active={billingInterval === "monthly"}
                  onClick={() => setBillingInterval("monthly")}
                >
                  Monthly
                </IntakeIntervalButton>
                <IntakeIntervalButton
                  type="button"
                  $active={billingInterval === "annual"}
                  onClick={() => setBillingInterval("annual")}
                >
                  <span>Annual</span>
                  <IntakeAnnualIncentiveBadge>2 months free</IntakeAnnualIncentiveBadge>
                </IntakeIntervalButton>
              </IntakeIntervalToggle>
            </IntakeBillingIntervalRow>
          )}

          <PlanIncludesCallout aria-live="polite" aria-atomic="true">
            <PlanIncludesTitle>
              Included with {planIncludesTitleName}
            </PlanIncludesTitle>
            {planIncludesLines.length > 0 ? (
              <>
                {planIncludesDoorLine && (
                  <PlanIncludesCap>{planIncludesDoorLine}</PlanIncludesCap>
                )}
                {planIncludesFeatureLines.length > 0 ? (
                  <PlanIncludesList>
                    {planIncludesFeatureLines.map((line) => (
                      <li key={line}>
                        <PlanIncludesBullet aria-hidden="true" />
                        <PlanIncludesItemText>{line}</PlanIncludesItemText>
                      </li>
                    ))}
                  </PlanIncludesList>
                ) : null}
              </>
            ) : (
              <PlanIncludesEmpty>
                Feature list unavailable for this plan. See pricing on the
                website or contact support.
              </PlanIncludesEmpty>
            )}
            <PlanIncludesFooter>
              {isFreePlan
                ? "No payment for the Free Gateway. Your account is created when you submit so you can start immediately. If you sign out, use Forgot password on the login page to set a password for signing in again."
                : "Checkout and subscription billing are completed in Stripe."}
            </PlanIncludesFooter>
          </PlanIncludesCallout>

          <Spacer $marginTop="2rem" />

          <Spacer $marginTop="3rem" />

          <Form.Item
            name="name"
            label="Full Name"
            rules={[
              { required: true, message: "Please enter your full name" },
              { min: 2, message: "Name must be at least 2 characters" },
            ]}
          >
            <Input size="large" placeholder="John Smith" />
          </Form.Item>

          <Spacer $marginTop="2rem" />

          <Form.Item
            name="email"
            label="Contact Email"
            rules={[
              { required: true, message: "Please enter your email address" },
              { type: "email", message: "Please enter a valid email address" },
            ]}
            help="Implementation instructions, receipts, and tenant inquiry data will be sent to this email"
          >
            <Input size="large" placeholder="you@email.com" />
          </Form.Item>

          {false && (
            <Form.Item
              name="serviceRequirements"
              label="Tenant Criteria & Requirements"
              help="Describe any specific requirements or criteria for tenants you accept"
            >
              <TextArea
                rows={4}
                placeholder="E.g., Credit score requirements, income requirements, pet policies, lease terms, background checks, etc."
              />
            </Form.Item>
          )}

          {false && (
            <Form.Item
              name="chatbotQuestions"
              label="Custom Intake Questions"
              help={`All standard questions related to property management will be included automatically. This section is only for any unique or custom questions you'd like to ask potential tenants. Industry standards for rental applications will be followed.`}
            >
              <TextArea
                rows={4}
                placeholder="E.g., Previous rental history, employment verification, references, move-in timeline, etc."
              />
            </Form.Item>
          )}
        </FormSection>

        <Spacer $marginTop="1.25rem" />

        {!isFreePlan && (
          <PaymentNotice>
            <PaymentNoticeText>
              <PaymentNoticeSentence>
                You will continue to Stripe Checkout.
              </PaymentNoticeSentence>
              <PaymentNoticeSentence>
                Price, renewal, and any promotional terms are shown there
                before you confirm payment.
              </PaymentNoticeSentence>
            </PaymentNoticeText>
          </PaymentNotice>
        )}

        <Spacer $marginTop="2rem" />

        <TermsAgreementContainer>
          <Form.Item
            name="agreeToTerms"
            valuePropName="checked"
            rules={[
              {
                validator: (_, value) =>
                  value
                    ? Promise.resolve()
                    : Promise.reject(
                        new Error(
                          "You must agree to the Terms of Use and Privacy Policy"
                        )
                      ),
              },
            ]}
          >
            <StyledCheckbox>
              <span>
                I agree to the{" "}
                <Link to={APP_TERMS_ROUTE} target="_blank">
                  Terms of Use
                </Link>{" "}
                and{" "}
                <Link to="/privacy" target="_blank">
                  Privacy Policy
                </Link>
              </span>
            </StyledCheckbox>
          </Form.Item>
        </TermsAgreementContainer>

        <ButtonContainer>
          <StyledSpace size="middle">
            <StyledButton
              size="large"
              onClick={handleModalClose}
              variant="text"
            >
              Cancel
            </StyledButton>
            <SubmitButton
              type="submit"
              size="large"
              isLoading={loading}
              disabled={!formValid || loading}
            >
              Continue
            </SubmitButton>
          </StyledSpace>
        </ButtonContainer>
      </StyledForm>
      </StyledModal>
    </>
  );
};

const StyledModal = styled(Modal)`
  .ant-modal-content {
    padding: 0;
    background: var(--color-bg-secondary) !important;
    border-radius: 20px;
    box-shadow:
      0 20px 60px var(--shadow-light),
      0 8px 32px rgba(0, 0, 0, 0.1);
    border: 1px solid var(--color-border-green);
    overflow: hidden;
  }

  @media (max-width: 480px) {
    .ant-modal-content {
      border-radius: 16px;
    }
  }

  .ant-modal-header {
    margin-bottom: 0;
    padding: 1.85rem 3.5rem 1.95rem 2.35rem;
    background: linear-gradient(
      160deg,
      var(--color-primary-dark) 0%,
      #2c5f2d 42%,
      var(--color-primary) 100%
    );
    border-bottom: none;
    border-radius: 20px 20px 0 0;
    position: relative;
    box-shadow: 0 10px 28px -14px rgba(0, 0, 0, 0.35);

    &::after {
      content: "";
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      height: 12px;
      background: linear-gradient(
        180deg,
        rgba(0, 0, 0, 0.06) 0%,
        transparent 100%
      );
      pointer-events: none;
    }
  }

  @media (max-width: 768px) {
    .ant-modal-header {
      padding: 1.5rem 3.25rem 1.65rem 1.75rem;
      border-radius: 20px 20px 0 0;
    }
  }

  @media (max-width: 480px) {
    .ant-modal-header {
      padding: 1.35rem 2.85rem 1.5rem 1.25rem;
      border-radius: 16px 16px 0 0;
    }
  }

  .ant-modal-body {
    padding: 0;
    background: linear-gradient(
      180deg,
      #fafdfb 0%,
      var(--color-bg-secondary) 28px
    );
  }

  .ant-modal-title {
    background: none !important;
    width: 100%;
    margin: 0 !important;
    padding: 0 !important;
    line-height: normal !important;
  }

  .ant-select-arrow {
    top: 60%;
    color: var(--color-primary) !important;
  }

  .ant-modal-close {
    display: flex !important;
    align-items: center;
    justify-content: center;
    z-index: 10;
    top: 1.1rem;
    right: 1.1rem;
    width: 40px;
    height: 40px;
    background: transparent !important;
    border: none !important;
    border-radius: 0 !important;
    font-size: 1.1rem;
    color: white !important;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    backdrop-filter: none !important;
  }

  @media (max-width: 480px) {
    .ant-modal-close {
      top: 0.85rem;
      right: 0.75rem;
      width: 36px;
      height: 36px;
    }
  }

  .ant-modal-close .anticon {
    color: white !important;
    font-size: 1.25rem;
  }

  .ant-modal-close svg {
    color: white !important;
    fill: white !important;
  }

  .ant-modal-close:hover {
    color: var(--color-primary-dark) !important;
    background: transparent !important;
    border-color: transparent !important;
    box-shadow: none !important;
  }

  .ant-modal-close:hover .anticon {
    color: var(--color-primary-dark) !important;
  }

  .ant-modal-close:hover svg {
    color: var(--color-primary-dark) !important;
    fill: var(--color-primary-dark) !important;
  }
`;

const ModalHeaderInner = styled.div`
  max-width: 100%;
  padding-right: 0.25rem;
`;

const ModalEyebrow = styled.span`
  display: block;
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.72);
  font-family: var(--font-secondary);
  margin-bottom: 0.4rem;
`;

const modalHeadlineTitleSize = css`
  font-size: clamp(1.4rem, 4.2vw, 1.9rem);
`;

const ModalHeadline = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.25rem 0.35rem;
  line-height: 1.12;
  text-shadow: 0 1px 18px rgba(0, 0, 0, 0.18);
`;

const ModalHeadlineBrand = styled.span`
  ${modalHeadlineTitleSize}
  font-weight: 700;
  font-family: var(--font-primary);
  color: #fff;
  letter-spacing: -0.03em;
`;

const ModalHeadlineSep = styled.span`
  ${modalHeadlineTitleSize}
  font-weight: 600;
  font-family: var(--font-primary);
  color: rgba(255, 255, 255, 0.65);
`;

const ModalHeadlineTier = styled.span`
  ${modalHeadlineTitleSize}
  font-weight: 700;
  font-family: var(--font-secondary);
  color: #fff;
  letter-spacing: -0.03em;
`;

const StyledForm = styled(Form)`
  .ant-form-item {
    margin-bottom: 2rem;
  }

  /* Tight gap to monthly/annual toggle; default 2rem is too large there */
  .intake-select-plan-with-toggle.ant-form-item {
    margin-bottom: 0.25rem;
  }

  .ant-form-item-label > label {
    color: var(--color-text);
    font-weight: 600;
    font-size: 1.125rem;
    font-family: var(--font-secondary);
    letter-spacing: -0.01em;
    margin-bottom: 0.75rem;
  }

  .ant-form-item-explain {
    color: rgba(0, 0, 0, 0.85);
    font-size: 0.9rem;
    margin-top: 0.5rem;
  }

  .ant-form-item-help {
    color: #444;
    font-size: 0.95rem;
    margin-top: 0.5rem;
    font-family: var(--font-secondary);
  }

  /* Help/tooltips (not errors) - force override */
  .ant-form-item .ant-form-item-help:not(.ant-form-item-explain-error),
  .ant-form-item-explain.ant-form-item-help:not(.ant-form-item-explain-error) {
    color: #444 !important;
    font-size: 0.95rem;
    margin-top: 0.5rem;
    font-family: var(--font-secondary);
  }

  /* Error messages */
  .ant-form-item-explain-error {
    color: var(--color-error) !important;
    font-size: 0.95rem;
    margin-top: 0.5rem;
    font-family: var(--font-secondary);
    text-align: center;
  }

  .ant-select-selection-placeholder,
  input::placeholder,
  textarea::placeholder {
    color: var(--color-text-muted);
    font-family: var(--font-secondary);
  }

  .ant-checkbox-wrapper {
    color: var(--color-text);
    font-family: var(--font-secondary);
    font-weight: 500;
  }

  .ant-checkbox-checked .ant-checkbox-inner {
    background-color: var(--color-primary);
    border-color: var(--color-primary);
  }

  .ant-checkbox-inner {
    background-color: var(--color-bg-secondary);
    border-color: var(--color-border-medium);
    border-radius: 6px;
  }

  .ant-select-selector {
    background-color: var(--color-bg-secondary) !important;
    border: 2px solid var(--color-border-medium) !important;
    border-radius: 12px !important;
    padding: 0.875rem 1rem !important;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
  }

  .ant-select-selector:hover {
    border-color: var(--color-primary) !important;
    box-shadow: 0 0 0 3px rgba(205, 175, 99, 0.1) !important;
  }

  .ant-select-focused .ant-select-selector {
    border-color: var(--color-primary) !important;
    box-shadow: 0 0 0 3px rgba(205, 175, 99, 0.15) !important;
  }

  .ant-select-selection-item {
    color: var(--color-text) !important;
    font-family: var(--font-secondary);
    font-weight: 500;
  }

  /* Plan picker: allow multi-line label so price/units never sit under the caret */
  .intake-plan-select.ant-select-single {
    height: auto !important;
    min-height: 40px;
  }

  .intake-plan-select.ant-select-single:not(.ant-select-customize-input)
    .ant-select-selector {
    height: auto !important;
    min-height: 40px !important;
    align-items: center !important;
  }

  .intake-plan-select .ant-select-selection-item {
    display: flex !important;
    align-items: flex-start !important;
    white-space: normal !important;
    overflow: visible !important;
    text-overflow: clip !important;
    line-height: 1.35 !important;
    align-self: center !important;
    padding-inline-end: 2rem !important;
  }

  .intake-plan-select .ant-select-arrow {
    top: 50% !important;
    inset-inline-end: 14px !important;
  }

  .ant-input,
  .ant-input-affix-wrapper,
  .ant-input-status-error,
  .ant-input-status-error .ant-input,
  .ant-input-status-error .ant-input-affix-wrapper,
  .ant-input-affix-wrapper-status-error,
  .ant-input-affix-wrapper-status-error .ant-input,
  .ant-select-selector,
  textarea,
  input {
    background: var(--color-bg-secondary) !important;
    color: var(--color-text) !important;
    border: 2px solid var(--color-border-medium) !important;
    border-radius: 12px !important;
    padding: 0.875rem 1rem !important;
    font-size: 1rem !important;
    font-family: var(--font-secondary) !important;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
  }

  .ant-input:focus,
  .ant-input-affix-wrapper:focus,
  .ant-input-affix-wrapper-focused,
  textarea:focus,
  input:focus {
    border-color: var(--color-primary) !important;
    box-shadow: 0 0 0 3px rgba(205, 175, 99, 0.15) !important;
    outline: none !important;
  }

  /* All custom styles for .ant-input-group-addon and related selectors are removed. Use Ant Design defaults for the website input field. */

  .ant-form-item-label > label[for="practiceAreas"] {
    font-size: 1.125rem !important;
    font-weight: 600 !important;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  /* Fix dropdown text color */
  .ant-select-dropdown {
    background: var(--color-bg-secondary) !important;
    border: 1px solid var(--color-border-light) !important;
    border-radius: 12px !important;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12) !important;
  }

  .ant-select-item {
    color: var(--color-text) !important;
    background: var(--color-bg-secondary) !important;
    font-family: var(--font-secondary) !important;
  }

  .ant-select-item-option {
    color: var(--color-text) !important;
    background: var(--color-bg-secondary) !important;
    padding: 0.75rem 1rem !important;
  }

  .ant-select-item-option-content {
    color: var(--color-text) !important;
    font-weight: 500;
  }

  .intake-plan-select-dropdown .ant-select-item-option-content {
    display: flex !important;
    align-items: flex-start !important;
    min-height: 0;
  }

  .ant-select-item-option-active {
    background-color: var(--color-bg-tertiary) !important;
    color: var(--color-text) !important;
  }

  .ant-select-item-option-selected {
    background-color: var(--color-primary-glow) !important;
    color: var(--color-primary) !important;
    font-weight: 600;
  }
`;

const PlanOptionContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 0.25rem;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
`;

const PlanOptionName = styled.span`
  font-weight: 600;
  font-size: 1rem;
  color: var(--color-text);
  line-height: 1.35;
  width: 100%;
`;

const PlanOptionDetails = styled.span`
  font-size: 0.875rem;
  color: var(--color-text-light);
  font-weight: 400;
  width: 100%;
  min-width: 0;
  white-space: normal;
  overflow-wrap: break-word;
  word-break: break-word;
  line-height: 1.35;
  text-align: left;
`;

const FormSection = styled.div`
  margin: 0;
  padding: 2.5rem;
  padding-top: 1.75rem;
  padding-bottom: 0.5rem;

  @media (max-width: 768px) {
    padding: 2rem 1.5rem;
    padding-top: 1.5rem;
  }

  @media (max-width: 480px) {
    padding: 1.5rem 1rem;
    padding-top: 1.35rem;
  }
`;

const PlanIncludesCallout = styled.div`
  padding: 0.75rem 0.9rem;
  margin: 0.35rem 0 1.1rem;
  background: var(--color-bg-primary);
  border: 1px solid var(--color-border-light);
  border-radius: 10px;
  color: var(--color-text);
  box-shadow: 0 1px 0 rgba(44, 95, 45, 0.06);
`;

const PlanIncludesTitle = styled.p`
  margin: 0 0 0.45rem 0;
  font-size: 0.8125rem;
  font-weight: 600;
  font-family: var(--font-secondary);
  color: var(--color-text-muted);
  line-height: 1.3;
  letter-spacing: 0.01em;
`;

const PlanIncludesCap = styled.div`
  display: inline-block;
  margin: 0 0 0.45rem 0;
  padding: 0.2rem 0.5rem;
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  font-family: var(--font-secondary);
  color: var(--color-primary-darker);
  background: rgba(44, 95, 45, 0.09);
  border-radius: 6px;
  line-height: 1.25;
`;

const PlanIncludesList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  font-size: 0.5625rem;
  font-family: var(--font-secondary);
  font-weight: 500;
  line-height: 1.45;
  color: var(--color-text-light);

  li {
    display: flex;
    align-items: flex-start;
    gap: 0.35rem;
    margin: 0;
    padding: 0.28rem 0;
    border-bottom: 1px solid rgba(0, 0, 0, 0.05);

    &:last-child {
      border-bottom: none;
      padding-bottom: 0;
    }

    &:first-child {
      padding-top: 0;
    }
  }
`;

/** Dot centered on first line: half the line box (1.45) minus half the 3px mark. */
const PlanIncludesBullet = styled.span`
  width: 3px;
  height: 3px;
  flex-shrink: 0;
  border-radius: 50%;
  background: var(--color-primary);
  opacity: 0.85;
  margin-top: calc(0.725em - 1.5px);
`;

const PlanIncludesItemText = styled.span`
  flex: 1;
  min-width: 0;
`;

const PlanIncludesEmpty = styled.p`
  margin: 0 0 0.5rem 0;
  font-size: 0.75rem;
  font-family: var(--font-secondary);
  color: var(--color-text-light);
  line-height: 1.45;
`;

const PlanIncludesFooter = styled.p`
  margin: 0.55rem 0 0 0;
  padding-top: 0.5rem;
  border-top: 1px solid var(--color-border-light);
  font-size: 0.6875rem;
  font-family: var(--font-secondary);
  color: var(--color-text-muted);
  line-height: 1.4;
`;

const DisclaimerText = styled.p`
  font-size: 0.875rem;
  color: var(--color-text-light);
  margin: 1.5rem 10%;
  text-align: center;
  font-family: var(--font-secondary);
  line-height: 1.5;

  a {
    color: var(--color-primary);
    text-decoration: none;
    font-weight: 600;
    transition: color 0.3s ease;

    &:hover {
      color: var(--color-primary-dark);
      text-decoration: underline;
    }
  }
`;

const PaymentNotice = styled.div`
  display: block;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  margin: 2rem 0;
  margin-bottom: 0;
  padding: 0.875rem 1.5rem;
  background: linear-gradient(
    135deg,
    rgba(44, 95, 45, 0.1) 0%,
    rgba(44, 95, 45, 0.05) 100%
  );
  border: 1px solid var(--color-primary-light);
  border-left: 0px;
  border-right: 0px;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      45deg,
      transparent 30%,
      rgba(255, 255, 255, 0.1) 50%,
      transparent 70%
    );
    transform: translateX(-100%);
    transition: transform 0.6s ease;
  }

  &:hover::before {
    transform: translateX(100%);
  }
`;

const PaymentNoticeText = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 40rem;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.15rem;
  text-align: center;
`;

const PaymentNoticeSentence = styled.p`
  margin: 0;
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1.35;
  font-family: var(--font-secondary);
  color: rgba(0, 0, 0, 0.85);
  word-wrap: break-word;
  overflow-wrap: break-word;
  max-width: 100%;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 2rem;
  padding: 0 2.5rem 2.5rem 2.5rem;
  gap: 1.5rem;
`;

const StyledSpace = styled(Space)`
  gap: 1.5rem !important;
`;

const StyledButton = styled(Button)`
  background-color: var(--color-bg-secondary);
  color: var(--color-text);
  border: 2px solid var(--color-border-medium);
  border-radius: 12px;
  padding: 0.875rem 2rem;
  font-size: 1rem;
  font-weight: 600;
  font-family: var(--font-secondary);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  min-width: 120px;

  &:hover {
    background-color: var(--color-bg-tertiary);
    border-color: var(--color-primary);
    color: var(--color-primary);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px var(--shadow-light);
  }
`;

const SubmitButton = styled(Button)`
  background: var(--gradient-primary) !important;
  border: none !important;
  min-width: 140px;
  padding: 0.875rem 2rem;
  color: #fff !important;
  font-family: var(--font-secondary) !important;
  font-weight: 700 !important;
  font-size: 1rem !important;
  border-radius: 12px !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
  box-shadow: 0 4px 16px var(--shadow-green) !important;
  position: relative;
  overflow: hidden;

  &:disabled {
    background: #d1d5db !important;
    color: #9ca3af !important;
    box-shadow: none !important;
    cursor: not-allowed !important;
    opacity: 0.6 !important;
  }

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.18),
      transparent
    );
    transition: left 0.5s ease;
  }

  &:hover {
    background: var(--gradient-primary-dark) !important;
    transform: translateY(-2px) !important;
    box-shadow: 0 8px 24px var(--shadow-dark) !important;
    &::before {
      left: 100%;
    }
  }

  &:active {
    transform: translateY(0) !important;
  }
`;

const IntakeBillingIntervalRow = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 0.35rem;
  margin-bottom: 1.15rem;
`;

const IntakeIntervalToggle = styled.div`
  display: inline-flex;
  padding: 4px;
  border-radius: 9999px;
  background: #eef2ed;
  box-shadow: 0 2px 10px rgba(15, 23, 42, 0.06);
  border: 1px solid rgba(44, 95, 45, 0.12);
`;

const IntakeIntervalButton = styled.button`
  border: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  padding: 0.5rem 1.15rem;
  border-radius: 9999px;
  font-size: 0.9rem;
  font-weight: 600;
  font-family: var(--font-secondary, "Inter", sans-serif);
  color: #4b5563;
  background: transparent;
  transition: background 0.2s ease, color 0.2s ease, box-shadow 0.2s ease;

  ${(p) =>
    p.$active
      ? `
    background: #2c5f2d;
    color: #fff;
    box-shadow: 0 2px 8px rgba(44, 95, 45, 0.3);
  `
      : `
    &:hover {
      color: #2c5f2d;
    }
  `}
`;

const IntakeAnnualIncentiveBadge = styled.span`
  display: inline-block;
  font-size: 0.64rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  padding: 0.2rem 0.45rem;
  border-radius: 6px;
  background: #cdaf63;
  color: #1f1b14;
  line-height: 1.2;
`;

// Add styled component for the dropdown row
const PlanBillingRow = styled.div`
  display: flex;
  width: 100%;

  > * {
    flex: 1;
    min-width: 0;
  }

  @media (max-width: 600px) {
    flex-direction: column;
    gap: 0.75rem;
  }
`;

// Add styled components for the terms agreement section
const TermsAgreementContainer = styled.div`
  background: #fafafa;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 1.25rem;
  margin: 0 auto 0 auto;
  max-width: 100%;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;

  @media (max-width: 600px) {
    padding: 1rem;
  }

  .ant-form-item {
    margin-bottom: 0rem;
    width: 100%;
    text-align: center;
  }

  .ant-form-item-explain-error {
    font-size: 0.9rem;
    text-align: center;
    margin-top: 0.5rem;
    white-space: nowrap;
    @media (max-width: 600px) {
      font-size: 0.7rem !important;
    }
    @media (max-width: 480px) {
      font-size: 0.6rem !important;
    }
  }
`;

const StyledCheckbox = styled(Checkbox)`
  .ant-checkbox-wrapper {
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    flex-wrap: wrap;
    font-family: var(--font-secondary);
    font-size: 0.9rem;
    font-weight: 500;
    color: #374151;
    line-height: 1.7;
    justify-content: center;
    text-align: center;
    width: 100%;
    white-space: normal;

    .ant-checkbox {
      flex-shrink: 0;
      margin-top: 0.1rem;
    }

    span {
      flex: 1 1 auto;
      word-wrap: break-word;
      overflow-wrap: anywhere;
      text-align: center;
      max-width: 100%;
      min-width: 0;
      hyphens: auto;
      display: block;
    }

    @media (max-width: 600px) {
      font-size: 0.9rem !important;
      justify-content: flex-start;
      text-align: left;
      gap: 0.5rem;

      span {
        font-size: 0.9rem !important;
        text-align: left !important;
        width: calc(100% - 28px);
      }
    }

    @media (max-width: 480px) {
      font-size: 0.9rem !important;

      span {
        font-size: 0.9rem !important;
      }
    }
  }

  .ant-checkbox {
    margin: 0;
    flex-shrink: 0;
    top: 0;
  }

  .ant-checkbox-inner {
    border-color: #d1d5db;
    border-radius: 4px;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    width: 20px;
    height: 20px;
    position: relative;
    &::after {
      border: 2px solid #fff;
      border-left: 0;
      border-top: 0;
      content: "";
      height: 10px;
      left: 6px;
      position: absolute;
      top: 2px;
      transform: rotate(45deg);
      width: 5px;
      display: none;
    }
  }

  .ant-checkbox-checked .ant-checkbox-inner {
    background-color: var(--color-primary);
    border-color: var(--color-primary);
    &::after {
      display: block;
    }
  }

  span {
    flex: 1;
    line-height: 1.4;
    white-space: nowrap;
    overflow: visible;
    text-align: center !important;

    @media (max-width: 600px) {
      font-size: 0.8rem !important;
      text-align: center !important;
    }
  }

  a {
    color: var(--color-primary);
    text-decoration: none;
    font-weight: 600;
    transition: color 0.3s ease;

    &:hover {
      color: var(--color-primary-dark);
      text-decoration: underline;
    }
  }
`;

export default IntakeModal;
